import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import BranchAddEditModal, { type BranchFormData } from '../../../../components/BranchAddEditModal/BranchAddEditModal';
import BranchRosterModal from '../../../../components/BranchRosterModal/BranchRosterModal';
import BranchAbsencePolicyModal from '../../../../components/BranchAbsencePolicyModal/BranchAbsencePolicyModal';
// Epic AB#1824 — The per-branch BranchHoursModal editor is DECOMMISSIONED.
// Branch cards now show a read-only `EffectiveHoursReadOnly` summary with a
// link that deep-links to the matrix on /my-business/business-details > Hours.
// Legacy BranchHoursModal import + state removed — see AB#1831 AC-1.
import { EffectiveHoursReadOnly } from '../../../../components/BusinessHoursMatrix';
import { useEffectiveBranchHoursQuery } from '../../../../hooks/useBusinessHoursMatrix';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import { interpolate } from '../../../../components/BusinessHoursMatrix/interpolate';
import BranchMoreMenu from './BranchMoreMenu';
import { useBranches } from '../../../../hooks/useBranches';
import { useStaff } from '../../../../hooks/useStaff';
import { useCurrentSubscription } from '../../../../hooks/useCurrentSubscription';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useToast } from '../../../../contexts/ToastContext';
import { queryKeys } from '../../../../lib/queryKeys';
import type { BranchDto, BusinessStaffDto } from '../../../../types/api-responses';
import styles from './Section.module.css';
import b from './BranchesSection.module.css';

/**
 * CR-2026-05-10 Phase 3 — Per-tier branch caps. The BE is the source of
 * truth (enforced in `CreateBranchCommandHandler`); this mirror table
 * drives the FE's "X of Y" hint and the upgrade card. Keep in sync with
 * `Devanza.Core.Entities.SubscriptionPlan.MaxBranches` seed values.
 */
const PLAN_BRANCH_CAPS: Record<string, number> = {
    Starter: 1,
    Growth: 3,
    Pro: 10,
    Enterprise: Infinity,
};
const DEFAULT_PLAN_NAME = 'Starter';
const DEFAULT_BRANCH_CAP = PLAN_BRANCH_CAPS[DEFAULT_PLAN_NAME];

/** Plan-tier ladder for upgrade-card "nextPlan / nextCap" resolution.
 *  Keep in lock-step with `PLAN_BRANCH_CAPS`. PR #1312 Omar Finding #2 fix. */
const BRANCH_TIER_LADDER: Array<{ plan: string; cap: number }> = [
    { plan: 'Starter', cap: PLAN_BRANCH_CAPS.Starter },
    { plan: 'Growth', cap: PLAN_BRANCH_CAPS.Growth },
    { plan: 'Pro', cap: PLAN_BRANCH_CAPS.Pro },
    { plan: 'Enterprise', cap: PLAN_BRANCH_CAPS.Enterprise },
];

function resolveNextBranchTier(currentPlan: string): { plan: string; cap: number } {
    const i = BRANCH_TIER_LADDER.findIndex(t => t.plan === currentPlan);
    if (i < 0 || i >= BRANCH_TIER_LADDER.length - 1) {
        return BRANCH_TIER_LADDER[BRANCH_TIER_LADDER.length - 1];
    }
    return BRANCH_TIER_LADDER[i + 1];
}

/**
 * AB#1675 — Branches sub-section (My Business shell).
 *
 * List + CRUD wired to the live `salon-owner/branches` controller. Reuses
 * the existing BranchAddEditModal (with map + autocomplete + Zod validation)
 * and DeleteModal so a salon owner has the same add/edit experience whether
 * they came in from the legacy /branches page or the new My Business shell.
 *
 * Polish goals (parity with BusinessDetailsSection):
 *  - Same dark Fresha tokens (Section.module.css)
 *  - Loading / error / empty / list states all rendered inline (no full-page
 *    redirect, no second-class placeholder)
 *  - Card-per-branch with status + HQ pills, edit/delete on the right
 *  - Empty state has a working primary CTA (was a dead button before)
 *  - RTL-aware (modal already RTL; card actions flip via :global([dir='rtl']))
 *
 * Not in scope here, follow-up PBIs:
 *  - Plan-tier branch cap surfaced at the toolbar with an upgrade hint
 *  - Drag-to-reorder (hours-page pattern) for primary-vs-secondary
 *  - i18n key extraction (strings still hardcoded; matches sibling sections
 *    until LanguageContext keys for /my-business land in one batch)
 */

/* ── Local row shape that's easy to feed into the modal ───────────── */
interface BranchRow extends BranchDto {
    /** Pretty-printed address used in the card body. */
    formattedAddress: string;
}

function toRow(dto: BranchDto): BranchRow {
    return {
        ...dto,
        formattedAddress: [dto.streetAddress, dto.city, dto.state, dto.country]
            .filter(s => s && s.trim().length > 0)
            .join(', '),
    };
}

function emptyModalData(): BranchFormData {
    return {
        Branch: '',
        BranchNameAr: '',
        StreetAddress: '',
        City: '',
        State: '',
        Country: '',
        PhoneNumber: '',
        Status: true,
        Headquarters: false,
        Description: '',
        Latitude: undefined,
        Longitude: undefined,
    };
}

function rowToModalData(row: BranchRow): BranchFormData {
    return {
        Branch: row.name ?? '',
        BranchNameAr: row.nameAr ?? '',
        StreetAddress: row.streetAddress ?? '',
        City: row.city ?? '',
        State: row.state ?? '',
        Country: row.country ?? '',
        PhoneNumber: row.phoneNumber ?? '',
        Status: row.active,
        Headquarters: row.primaryHeadQuarter,
        Description: row.description ?? '',
        Latitude: row.latitude,
        Longitude: row.longitude,
    };
}

const BranchesSection: React.FC = () => {
    const { t, language } = useLanguage();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const { getBranches, createBranch, updateBranch, deleteBranch, loading } = useBranches();
    // CR-2026-05-16-v2.5 — staff list + setStaffBranches feed the Roster modal.
    const { getBusinessStaff, setStaffBranches } = useStaff();

    // CR-2026-05-10 Phase 3 — read the active subscription so the toolbar
    // can render "X of Y (PlanName)" and the card knows when to swap the
    // "+ Add branch" button for the upgrade hint.
    const { data: subscription } = useCurrentSubscription();
    const planName = subscription?.planName ?? DEFAULT_PLAN_NAME;
    const branchCap = PLAN_BRANCH_CAPS[planName] ?? DEFAULT_BRANCH_CAP;

    // Invalidate the TanStack-cached branches list so the My Business sidebar
    // dot recomputes after any add / update / delete.
    const invalidateBranchesCache = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.branches.list() });
    }, [queryClient]);

    const [rows, setRows] = useState<BranchRow[] | null>(null); // null until first fetch
    const [fetchError, setFetchError] = useState<string | null>(null);

    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [isAdd, setIsAdd] = useState(true);
    const [editingRow, setEditingRow] = useState<BranchRow | null>(null);
    const [hasUnsavedModalChanges, setHasUnsavedModalChanges] = useState(false);

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deletingRow, setDeletingRow] = useState<BranchRow | null>(null);

    // CR-2026-05-16-v2.5 — Roster modal state. Lazy-loads the staff list when
    // the modal opens so we don't pay the round trip until needed.
    const [rosterBranch, setRosterBranch] = useState<BranchRow | null>(null);
    // AB#2024 — per-branch break & absence rules editor.
    const [policyBranch, setPolicyBranch] = useState<{ id: number; name: string } | null>(null);
    const [rosterStaff, setRosterStaff] = useState<BusinessStaffDto[]>([]);
    const [rosterLoading, setRosterLoading] = useState(false);

    const openRoster = useCallback(async (row: BranchRow) => {
        setRosterLoading(true);
        try {
            // Pull all staff (incl. inactive — the modal filters to active itself).
            // BE caps pageSize at 50; mirror that.
            const res = await getBusinessStaff({ pageSize: 50 });
            setRosterStaff(res?.staff ?? []);
            setRosterBranch(row);
        } catch (err) {
            showToast(t('mb.branches.toast.teamLoadFailed'), 'error');
        } finally {
            setRosterLoading(false);
        }
    }, [getBusinessStaff, showToast, t]);

    const closeRoster = useCallback(() => {
        setRosterBranch(null);
        setRosterStaff([]);
    }, []);

    // Epic AB#1824 — Effective-hours readout per branch. Single GET shared
    // across all branch cards; the per-card panel slices into `branches[]`
    // by branchId.
    const { data: effectiveHours } = useEffectiveBranchHoursQuery();

    const loadBranches = useCallback(async () => {
        setFetchError(null);
        try {
            const res = await getBranches();
            setRows(res.branches.map(toRow));
        } catch (err) {
            // O3 — never surface the raw (English, technical) error text.
            const msg = t('mb.branches.loadFailed');
            setFetchError(msg);
            // Toast on first failure only — repeat fetches inside this session
            // surface the inline retry banner instead.
            if (rows === null) showToast(msg, 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getBranches, showToast, t]);

    useEffect(() => {
        loadBranches();
    }, [loadBranches]);

    /* ── Modal handlers ─────────────────────────────────────────────── */
    const openAdd = () => {
        setIsAdd(true);
        setEditingRow(null);
        setHasUnsavedModalChanges(false);
        setIsAddEditOpen(true);
    };

    const openEdit = (row: BranchRow) => {
        setIsAdd(false);
        setEditingRow(row);
        setHasUnsavedModalChanges(false);
        setIsAddEditOpen(true);
    };

    const closeAddEdit = () => {
        if (hasUnsavedModalChanges) {
            const ok = window.confirm(t('mb.common.discardChanges'));
            if (!ok) return;
        }
        setIsAddEditOpen(false);
        setHasUnsavedModalChanges(false);
    };

    const handleSave = async (form: BranchFormData) => {
        try {
            if (isAdd) {
                await createBranch({
                    name: form.Branch,
                    nameAr: form.BranchNameAr?.trim() ?? '',
                    streetAddress: form.StreetAddress,
                    city: form.City,
                    state: form.State,
                    country: form.Country,
                    phoneNumber: form.PhoneNumber,
                    latitude: form.Latitude,
                    longitude: form.Longitude,
                    primaryHeadQuarter: form.Headquarters,
                    active: form.Status,
                    description: form.Description ?? '',
                });
                showToast(t('mb.branches.toast.created'), 'success');
            } else if (editingRow) {
                await updateBranch(editingRow.id, {
                    id: editingRow.id,
                    name: form.Branch,
                    nameAr: form.BranchNameAr?.trim() ?? '',
                    streetAddress: form.StreetAddress,
                    city: form.City,
                    state: form.State,
                    country: form.Country,
                    phoneNumber: form.PhoneNumber,
                    latitude: form.Latitude,
                    longitude: form.Longitude,
                    primaryHeadQuarter: form.Headquarters,
                    active: form.Status,
                    description: form.Description ?? '',
                });
                showToast(t('mb.branches.toast.updated'), 'success');
            }
            invalidateBranchesCache();
            await loadBranches();
            setIsAddEditOpen(false);
            setHasUnsavedModalChanges(false);
        } catch (err) {
            showToast(t(isAdd ? 'mb.branches.toast.createFailed' : 'mb.branches.toast.updateFailed'), 'error');
        }
    };

    const openDeleteFromRow = (row: BranchRow) => {
        setDeletingRow(row);
        setIsAddEditOpen(false);
        setIsDeleteOpen(true);
    };

    const openDeleteFromModal = () => {
        if (!editingRow) return;
        setDeletingRow(editingRow);
        setIsAddEditOpen(false);
        setIsDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!deletingRow) return;
        try {
            await deleteBranch(deletingRow.id);
            showToast(t('mb.branches.toast.deleted'), 'success');
            setIsDeleteOpen(false);
            setDeletingRow(null);
            invalidateBranchesCache();
            await loadBranches();
        } catch (err) {
            showToast(t('mb.branches.toast.deleteFailed'), 'error');
        }
    };

    /* ── Render ─────────────────────────────────────────────────────── */
    const isInitialLoading = loading && rows === null;
    const showEmpty = !isInitialLoading && !fetchError && rows !== null && rows.length === 0;
    const showList = !isInitialLoading && !fetchError && rows !== null && rows.length > 0;

    return (
        <div>
            <header className={styles.header}>
                <h1>{t('mb.branches.title')}</h1>
                <p>{t('mb.branches.subtitle')}</p>
            </header>

            <div className={styles.intro}>
                <span className={styles.introIcon}>💡</span>
                <div>
                    <strong>{t('mb.branches.intro.strong')}</strong> {t('mb.branches.intro.body')}
                </div>
            </div>

            {/* Loading skeleton */}
            {isInitialLoading && (
                <ul className={b.cardList} aria-busy="true">
                    {[0, 1, 2].map(i => (
                        <li key={i} className={b.skeletonCard}>
                            <div className={`${b.skeletonLine} ${b.skeletonLineMed}`} />
                            <div className={`${b.skeletonLine} ${b.skeletonLineLong}`} />
                            <div className={`${b.skeletonLine} ${b.skeletonLineShort}`} />
                        </li>
                    ))}
                </ul>
            )}

            {/* Error banner with retry */}
            {fetchError && rows === null && (
                <div className={styles.intro} role="alert">
                    <span className={styles.introIcon}>⚠️</span>
                    <div>
                        <strong>{fetchError}</strong>{' '}
                        <button
                            type="button"
                            onClick={loadBranches}
                            className={styles.btn}
                            style={{ marginInlineStart: 12 }}
                        >
                            {t('mb.common.tryAgain')}
                        </button>
                    </div>
                </div>
            )}

            {/* Empty state — working primary CTA */}
            {showEmpty && (
                <div className={b.empty}>
                    <div className={b.emptyIcon} aria-hidden="true">📍</div>
                    <h2 className={b.emptyTitle}>{t('mb.branches.empty.title')}</h2>
                    <p className={b.emptyDesc}>{t('mb.branches.empty.desc')}</p>
                    <button type="button" className={styles.btnPrimary} onClick={openAdd}>
                        {t('mb.branches.empty.cta')}
                    </button>
                    <div className={b.emptyHint}>
                        {t('mb.branches.empty.hint')}{' '}
                        <Link to="/my-business/business-details">{t('mb.branches.empty.hintDetails')}</Link>
                        {' '}{t('mb.branches.empty.hintOr')}{' '}
                        <Link to="/my-business/services">{t('mb.branches.empty.hintServices')}</Link>
                    </div>
                </div>
            )}

            {/* Populated list */}
            {showList && (
                <>
                    <div className={b.toolbar}>
                        <div className={b.toolbarMeta}>
                            <span className={b.count}>
                                {interpolate(t('mb.branches.count'), {
                                    n: rows!.length,
                                    cap: Number.isFinite(branchCap) ? branchCap : '∞',
                                })}
                                {' '}
                                <span className={b.planPill}>{planName}</span>
                            </span>
                            <span>
                                {interpolate(t('mb.common.activeInactive'), {
                                    active: rows!.filter(r => r.active).length,
                                    inactive: rows!.filter(r => !r.active).length,
                                })}
                            </span>
                        </div>
                        {/* CR-2026-05-10 Phase 3 — Hide the Add CTA at-cap;
                            the upgrade-hint card below replaces it. */}
                        {rows!.length < branchCap && (
                            <button type="button" className={styles.btnPrimary} onClick={openAdd}>
                                {t('mb.branches.add')}
                            </button>
                        )}
                    </div>

                    {/* CR-2026-05-10 Phase 3 — Soft upgrade hint when at cap.
                        AB#1904 (W-1) — sharper copy + `?from=branches&feature=multi-branch`
                        attribution params so /subscription highlights the
                        recommended plan and App Insights can attribute
                        conversion. */}
                    {rows!.length >= branchCap && Number.isFinite(branchCap) && (() => {
                        // PR #1312 Omar Finding #2 — all strings tokenized through t()
                        // using the existing `subscription.cap.*` key family. nextPlan
                        // resolves up the BRANCH_TIER_LADDER; when the next tier is
                        // unlimited (Pro → Enterprise), swap to the *Unlimited body
                        // key to avoid rendering literal "Infinity" / "بلا حدود" inside
                        // a phrase that expects a finite numeral.
                        const nextTier = resolveNextBranchTier(planName);
                        const nextCapUnlimited = !Number.isFinite(nextTier.cap);
                        const bodyKey = nextCapUnlimited
                            ? 'subscription.cap.branchesBodyUnlimited'
                            : 'subscription.cap.branchesBody';
                        const title = t('subscription.cap.branchesTitle')
                            .replace('{n}', String(branchCap + 1));
                        const body = t(bodyKey)
                            .replace('{plan}', planName)
                            .replace('{cap}', String(branchCap))
                            .replace('{nextPlan}', nextTier.plan)
                            .replace('{nextCap}', nextCapUnlimited ? '' : String(nextTier.cap));
                        return (
                            <div className={b.upgradeCard}>
                                <div className={b.upgradeCardIcon} aria-hidden="true">✨</div>
                                <div className={b.upgradeCardBody}>
                                    <div className={b.upgradeCardTitle}>{title}</div>
                                    <div className={b.upgradeCardDesc}>{body}</div>
                                </div>
                                <Link
                                    to="/subscription?from=branches&feature=multi-branch"
                                    className={b.upgradeCardCta}
                                >
                                    {t('subscription.cap.cta')}
                                </Link>
                            </div>
                        );
                    })()}

                    <ul className={b.cardList}>
                        {rows!.map(row => (
                            <li key={row.id} className={b.card}>
                                <div className={b.cardMain}>
                                    <div className={b.cardNameRow}>
                                        <span className={b.cardName}>{language === 'ar' && row.nameAr ? row.nameAr : row.name}</span>
                                        {row.primaryHeadQuarter && (
                                            <span className={`${b.pill} ${b.hqPill}`}>{t('mb.branches.hq')}</span>
                                        )}
                                        <span
                                            className={`${b.pill} ${row.active ? b.statusActive : b.statusInactive}`}
                                        >
                                            {row.active ? t('mb.common.active') : t('mb.common.inactive')}
                                        </span>
                                    </div>
                                    {row.formattedAddress && (
                                        <div className={b.cardAddress}>{row.formattedAddress}</div>
                                    )}
                                    {row.phoneNumber && (
                                        <div className={b.cardPhone}>📞 <span dir="ltr">{row.phoneNumber}</span></div>
                                    )}
                                    {row.description && (
                                        <div className={b.cardDescription}>{row.description}</div>
                                    )}
                                </div>
                                <div className={b.cardActions}>
                                    {/* O3 (SO-28) — Edit is the primary action; the
                                        team + break-rule editors are secondary;
                                        Delete sits in the "More" menu, styled as
                                        destructive, and still goes through the
                                        DeleteModal confirm. */}
                                    <button
                                        type="button"
                                        className={`${b.iconBtn} ${b.iconBtnPrimary}`}
                                        onClick={() => openEdit(row)}
                                        aria-label={interpolate(t('mb.branches.editAria'), { name: row.name })}
                                    >
                                        {t('mb.common.edit')}
                                    </button>
                                    {/* CR-2026-05-16-v2.5 — who works at this branch
                                        (the same assignment is also editable per
                                        person in Manage Staff). */}
                                    <button
                                        type="button"
                                        className={b.iconBtn}
                                        onClick={() => openRoster(row)}
                                        disabled={rosterLoading}
                                        aria-label={interpolate(t('mb.branches.teamAria'), { name: row.name })}
                                    >
                                        {rosterLoading && rosterBranch?.id === row.id
                                            ? t('mb.common.loading')
                                            : t('mb.branches.team')}
                                    </button>
                                    {/* AB#2024 — per-branch break & absence rules. */}
                                    <button
                                        type="button"
                                        className={b.iconBtn}
                                        onClick={() => setPolicyBranch({ id: row.id, name: row.name })}
                                        aria-label={interpolate(t('mb.branches.breakRulesAria'), { name: row.name })}
                                    >
                                        {t('branch.absencePolicy.button')}
                                    </button>
                                    {/*
                                     * Epic AB#1824 / AB#1831 — Per-branch Hours
                                     * editor REMOVED. Hours are now managed from
                                     * the single matrix on Business Details → Hours.
                                     */}
                                    <BranchMoreMenu
                                        label={t('mb.common.more')}
                                        ariaLabel={interpolate(t('mb.branches.moreAria'), { name: row.name })}
                                        items={[
                                            {
                                                key: 'delete',
                                                label: t('mb.branches.delete'),
                                                destructive: true,
                                                onSelect: () => openDeleteFromRow(row),
                                            },
                                        ]}
                                    />
                                </div>
                                {/* Epic AB#1824 / AB#1831 — Read-only effective hours panel. */}
                                {effectiveHours && (() => {
                                    const entry = effectiveHours.branches.find(
                                        (be) => be.branchId === row.id,
                                    );
                                    if (!entry) return null;
                                    return (
                                        <div style={{ width: '100%', marginTop: 12 }}>
                                            <EffectiveHoursReadOnly
                                                branchEntry={entry}
                                                defaultRow={effectiveHours.defaultRow?.hours ?? []}
                                            />
                                        </div>
                                    );
                                })()}
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {isAddEditOpen && (
                <BranchAddEditModal
                    isOpen={isAddEditOpen}
                    onClose={closeAddEdit}
                    onSave={handleSave}
                    initialData={editingRow ? rowToModalData(editingRow) : emptyModalData()}
                    isAdd={isAdd}
                    loading={loading}
                    onDirtyChange={setHasUnsavedModalChanges}
                    onDeleteClicked={openDeleteFromModal}
                />
            )}

            {isDeleteOpen && (
                <DeleteModal
                    isOpen={isDeleteOpen}
                    onClose={() => {
                        setIsDeleteOpen(false);
                        setDeletingRow(null);
                    }}
                    onDelete={handleDelete}
                    branchName={deletingRow?.name || ''}
                />
            )}

            {/* CR-2026-05-16-v2.5 — Roster modal for bulk staff↔branch
                management from the branch axis. */}
            {rosterBranch && (
                <BranchRosterModal
                    isOpen={!!rosterBranch}
                    branch={rosterBranch}
                    allStaff={rosterStaff}
                    allBranches={rows ?? []}
                    onClose={closeRoster}
                    onSaved={async () => {
                        showToast(interpolate(t('mb.branches.toast.teamUpdated'), { name: rosterBranch.name }), 'success');
                    }}
                    setStaffBranches={setStaffBranches}
                />
            )}

            {/* AB#2024 — per-branch break & absence rules editor. */}
            {policyBranch && (
                <BranchAbsencePolicyModal
                    branch={policyBranch}
                    onClose={() => setPolicyBranch(null)}
                />
            )}

            {/* Epic AB#1824 / AB#1831 — BranchHoursModal DECOMMISSIONED.
                Hours edits all go through the matrix on Business Details. */}
        </div>
    );
};

export default BranchesSection;
